import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Crown, MapPin, Plane } from "lucide-react";
import { lienAnnonce, estNouvelleAnnonce, BadgeNouveau } from "@/lib/annonces";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RUBRIQUES = [
  {
    cle: "vip",
    nom: "FodindinClubVIP",
    accroche: "Le cercle privé — discrétion et haut de gamme",
    couleur: "#8B6F2A",
    icone: Crown,
    lien: "/categorie/vip",
  },
  {
    cle: "air",
    nom: "FodindinAir-Bengue",
    accroche: "Les annonces de la diaspora, Paris et au-delà",
    couleur: "#1F6F8B",
    icone: Plane,
    lien: "/air-bengue",
  },
];

const Carte = ({ annonce, couleur, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: index * 0.07 }}
  >
    <Link
      to={lienAnnonce(annonce)}
      data-testid={`une-annonce-${annonce.id}`}
      className="group block overflow-hidden rounded-2xl border border-[#E5DFD5] bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl"
    >
      <div className="relative h-56 overflow-hidden bg-[#EFE9DF]">
        {annonce.photo ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${annonce.photo}`}
            alt={annonce.titre}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center font-heading text-5xl font-extrabold text-white"
            style={{ backgroundColor: couleur }}>
            {(annonce.titre || "?").charAt(0)}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
        {estNouvelleAnnonce(annonce.date_debut) && (
          <span className="absolute left-3 top-3">
            <BadgeNouveau testid={`une-badge-nouveau-${annonce.id}`} />
          </span>
        )}
        {annonce.lieu && (
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
            <MapPin size={11} /> {annonce.lieu}
          </span>
        )}
      </div>
      <div className="space-y-1.5 p-4">
        <h4 className="font-heading text-base font-extrabold leading-tight text-stone-900 line-clamp-2">
          {annonce.titre}
        </h4>
        {annonce.texte && (
          <p className="text-xs leading-relaxed text-stone-500 line-clamp-2">{annonce.texte}</p>
        )}
      </div>
    </Link>
  </motion.div>
);

/**
 * « À la une » : dernières annonces ClubVIP et Air-Bengue publiées depuis l'administration.
 * Aperçu public uniquement — les numéros restent réservés aux abonnés de la rubrique.
 */
export const AnnoncesALaUne = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/accueil/a-la-une`, { params: { par_rubrique: 8 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ vip: [], air: [] }));
  }, []);

  if (!data) return null;
  const rubriques = RUBRIQUES.filter((r) => (data[r.cle] || []).length > 0);
  if (rubriques.length === 0) return null;

  return (
    <section className="bg-[#12100E] py-24" data-testid="section-a-la-une">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#E05638]">
          Dernières publications
        </span>
        <h2 className="mt-3 font-heading text-4xl font-extrabold text-white sm:text-5xl">
          À la une
        </h2>
        <p className="mt-3 max-w-2xl text-base text-stone-400">
          Les annonces ClubVIP et Air-Bengue tout juste publiées. Photos en clair pour tous,
          numéros réservés aux abonnés de la rubrique.
        </p>

        {rubriques.map((r) => {
          const Icone = r.icone;
          return (
            <div key={r.cle} className="mt-16" data-testid={`une-rubrique-${r.cle}`}>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h3 className="inline-flex items-center gap-2.5 font-heading text-2xl font-extrabold text-white">
                    <Icone size={22} style={{ color: r.couleur }} /> {r.nom}
                  </h3>
                  <p className="mt-1 text-sm text-stone-400">{r.accroche}</p>
                </div>
                <Link
                  to={r.lien}
                  data-testid={`une-voir-tout-${r.cle}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                >
                  Voir toutes les annonces <ArrowRight size={15} />
                </Link>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {data[r.cle].map((a, i) => (
                  <Carte key={a.id} annonce={a} couleur={r.couleur} index={i} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
